import React, { Suspense, useMemo } from 'react'
import { useSetAtom } from 'jotai'
import type { Contact } from '@node-space/types'
import { RowsSkeletonLoader } from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { createMFELazy } from 'utils/createMFELazy'
import { contactAtom } from './atoms/contactAtoms'

const ContactsLazy = createMFELazy(() => import('contacts/Contacts'), {
  mfeName: 'Contacts',
  testId: 'contacts-mfe-error',
})

export const ContactsMFE = () => {
  const { viewOnly } = useProfileContext() || {}
  const setContact = useSetAtom(contactAtom)
  const {
    enableAddAccountSheet,
    enableHybridAddress,
    enableUsLaunchRestrictions,
    enableUsRestrictedCurrencies,
    enableMaltaAccounts,
  } = useFeatureFlags()
  const disallowPaymentCurrencies = enableMaltaAccounts?.disallowPaymentCurrencies
  const { accountCapabilities, currentAccount } = useAccountsContext() || {}
  const {
    isLatamBankAccountCapabilityEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isMcMoveBeneficiaryCapabilityEnabled,
  } = accountCapabilities || {}
  const accountReference = currentAccount?.reference

  const featureFlags = useMemo(
    () => ({
      enableAddAccountSheet,
      enableHybridAddress,
      enableUsLaunchRestrictions,
      enableUsRestrictedCurrencies,
      disallowPaymentCurrencies,
      isLatamBankAccountCapabilityEnabled,
      isMcMoveBeneficiaryCapabilityEnabled,
      isUsdCRNowBeneficiaryCapabilityEnabled,
      isUsdFedNowBeneficiaryCapabilityEnabled,
      isUsdRtpBeneficiaryCapabilityEnabled,
    }),
    [
      enableAddAccountSheet,
      enableHybridAddress,
      enableUsLaunchRestrictions,
      enableUsRestrictedCurrencies,
      disallowPaymentCurrencies,
      isLatamBankAccountCapabilityEnabled,
      isMcMoveBeneficiaryCapabilityEnabled,
      isUsdCRNowBeneficiaryCapabilityEnabled,
      isUsdFedNowBeneficiaryCapabilityEnabled,
      isUsdRtpBeneficiaryCapabilityEnabled,
    ]
  )

  return (
    <Suspense fallback={<RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}>
      <ContactsLazy
        bffUrl={process.env.VITE_BASE_URL}
        accountReference={accountReference}
        viewOnly={viewOnly}
        featureFlags={featureFlags}
        onViewContact={(contact: Contact) => setContact(contact)}
      />
    </Suspense>
  )
}
